import classnames from 'classnames'
import React, { Fragment, ForwardedRef } from 'react'

// eslint-disable-next-line import/no-named-as-default
import ProductCTADS from '@src/components/blocks/product-cta-ds'
import GreyBoxesDS from '@src/components/blocks/product-cta-ds/grey-boxes-ds'
import { RenderErrorMessageDS } from '@src/components/blocks/product-cta-ds/product-message-ds'
import { ProductInfoDS } from '@src/components/blocks/product-info-ds'
import AirlineCompatibility from '@src/quince-components/src/atoms/airlineCompability/AirlineCompability'
import B2BSelfServeProductOfferMessage from '@src/quince-components/src/atoms/b2bSelfServeProductOfferMessage/B2BSelfServeProductOfferMessage'
import { BreadCrumb } from '@src/quince-components/src/atoms/breadCrumb'
import { Icon } from '@src/quince-components/src/atoms/icon'
import ProductDescription from '@src/quince-components/src/atoms/product-description/product-description'
import { ProductFormSubscriptionPurchaseSection } from '@src/quince-components/src/atoms/productForm/components/ProductFormSubscriptionPurchaseSection'
import HurryIcon from '@src/quince-components/src/atoms/productForm/icons/hurryIcon.inline.svg'
import { RightMiniPDPAddToCart } from '@src/quince-components/src/atoms/productForm/MiniPDPRightContainer'
import * as dsStyles from '@src/quince-components/src/atoms/productForm/product-form-ds.styles'
import {
  KlarnaWrapper,
  renderProductOption,
  SiblingProductOptions,
  ViewFullProductDetails,
} from '@src/quince-components/src/atoms/productForm/product-form-shared'
import { ProductFormProps } from '@src/quince-components/src/atoms/productForm/product-form.types'
import { ProductSubscriptionTermsNotice } from '@src/quince-components/src/atoms/productForm/product-subscription-terms-notice'
import { ProductDescFooter } from '@src/quince-components/src/atoms/productForm/ProductDescFooter'
import SizeChartButton from '@src/quince-components/src/atoms/productForm/SizeChartButton'
import { useProductForm } from '@src/quince-components/src/atoms/productForm/use-product-form'
import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector'
import { SiblingProductOptionType } from '@src/quince-components/src/atoms/siblingProductOptions'
import { SizeGuide } from '@src/quince-components/src/atoms/sizeGuide'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { AppQRModal } from '@src/quince-site-common/src/hooks/appQrModal'
import B2BProductPurchaseOptions from '@src/quince-site-common/src/organisms/b2b/b2b-product-purchase-options'

import {
  breadCrumbWrapperDsClass,
  errorMessageMiniPdpDsClass,
  freeSwatchDsClass,
  quickAtcModalDsClass,
} from './product-form-ds.styles'

const ProductFormDS = React.forwardRef(function ProductFormDSEl(
  props: ProductFormProps,
  ref: ForwardedRef<HTMLDivElement | null>,
) {
  const {
    t,
    productFormRef,
    showFullPDP,
    setShowFullPDP,
    productFormWrapperClass,
    moveAddToCartBtnToRight,
    showViewFullProductDetails,
    hideReviewCount,
    viewFullProductText,
    productInfoHeaderTextClassName,
    hideProductDescFooter,
    hideReviewLabel,
    productFormOptionsClass,
    productInfoReviewClassName,
    inStockMsgClass,
    showReadMoreInDesc,
    showInlineSizeChart,
    hideSizeChart,
    hideKlarna,
    hideKlarnaFull,
    isRecurringItemSubscriptionsExpEnabled,
    shouldKeepDefaultDesktopExpressPayRowMounted,
    optionNameSelectedValueMap,
    isVariantThirdParty,
    newColorGroupings,
    selectedVariant,
    showSizeChart,
    setShowSizeChart,
    showAirlineCompatibility,
    handleShopInApp,
    isAppQRModalOpen,
    handleCloseAppQRModal,
    showTruefitWidget,
    handleSizeChart,
    hideAirlineCompatbility,
    selectedVariantAvailableInventory,
    renderSwatchSelectionButton,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    showVariantAvailableInventory,
    showOnlySizeChart,
    hideSavings,
    productDetailsIcons,
    showProductDescription,
    deviceInfo,
    isPDP,
    isMiniPDPPage,
  } = useProductForm(props, ref, freeSwatchDsClass)

  const { productCTAType } = props

  // Width comes from the host wrapper's props.className; no local container width class.
  const classes = classnames(props.className, productFormWrapperClass, props.isQuickATCModal && quickAtcModalDsClass)

  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          className={dsStyles.optionGroupDsClass}
          labelClassName={dsStyles.labelDsClass}
          optionSelectorClassName={undefined}
          optionClassname={undefined}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
    }
  })

  let updatedProductOptions = props.productOptions
  if (newColorGroupings.length && props.product) {
    updatedProductOptions = productHelper.updateProductOptionsWithNewColorOptions(
      props.product,
      props.productOptions,
      newColorGroupings,
      t,
    ) as typeof props.productOptions
    updatedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
  }

  const colorOptionLastIndex = updatedProductOptions.findLastIndex(po => po.name == ProductOptionName.Color)

  const productOptions = updatedProductOptions
    .filter(po => !po.isHidden)
    .map((productOption, index, arr) => {
      const shouldReduceMarginBottom =
        (newColorGroupings.length && index + 1 <= arr.length - 1 && arr[index + 1]?.name == ProductOptionName.Color) ||
        false
      const shouldShowSwatchButton = colorOptionLastIndex === index && !props.isBuyNow
      return {
        node: renderProductOption({
          productOption,
          selectedOptions: props.selectedOptions,
          onOptionSelect: props.onOptionSelect,
          sizeChart: props.sizeChart,
          mergedSizeChart: props.mergedSizeChart,
          handleSizeChart,
          hideSizeChart: props.hideSizeChart ?? hideSizeChart,
          optionNameSelectedValueMap,
          renderSwatchSelectionButton: shouldShowSwatchButton ? renderSwatchSelectionButton : () => null,
          optionsContainerClassName: props.optionsContainerClassName,
          productFormOptionsClass,
          shouldReduceMarginBottom,
          showInlineSizeChart,
          showTruefitWidget,
          product: props.product,
          t,
          productPurchaseInfo: props.productPurchaseInfo,
          inputOptionError: props.inputOptionError,
          isB2BSelfService: props.isB2BSelfService,
          colorVariantRef: props.aboveFoldTrackingRefs?.colorVariantRef,
          sizeVariantRef: props.aboveFoldTrackingRefs?.sizeVariantRef,
          sizeOptionRef: props.sizeOptionRef,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }
    })

  const combinedProductOptions = [...(addOnOptions ?? []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const productInfoProps = {
    product: props.product,
    productInfoHeaderClassName: props.productInfoHeaderClassName,
    hideReviewSummary: props.hideReviewSummary,
    productTitle: props.title,
    formattedSalePrice: props.formattedSalePrice,
    formattedTraditionalPrice: props.formattedTraditionalPrice,
    isWishListed: props.isWishListed,
    avgRating: props.averageRating,
    reviewCount: props.reviewCount,
    onWishListToggle: props.onWishlistToggle,
    hideWishListIcon: true,
    onReviewSummaryClick: props.onReviewSummaryClick,
    savingPercentage: props.savingPercentage,
    brandName: props.brandName,
    brandLink: props.brandLink,
    traditionalDiscountMessage: props.traditionalDiscountMessage,
    formattedFestivalOfferPrice: props.formattedFestivalOfferPrice,
    showFullPDP,
    hideReviewCount,
    hideReviewLabel,
    productInfoHeaderTextClassName,
    productInfoReviewClassName,
    festivalPricePerUnit: props.festivalPricePerUnit,
    subscriptionOfferPricePerUnit: props.subscriptionOfferPricePerUnit,
    pricePerServing: selectedVariant.pricePerServing,
    traditionalPricePerServing: selectedVariant.traditionalPricePerServing,
    hideSavings,
    hideSalePrice: props.hideSalePrice,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    formattedSubscriptionPrice: props.formattedSubscriptionPrice,
    purchaseOption: props.purchaseOption,
    updatedDesignForV2: true,
    containerRef: props.aboveFoldTrackingRefs?.productInfoRef,
    isVariantThirdParty,
    selectedOptions: props.selectedOptions,
  }

  const greyBoxesProps = {
    product: props.product,
    productOptions: props.productOptions,
    selectedOptions: props.selectedOptions,
    selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
    productCTAType,
    isReturnable: props.isReturnable,
    subDescriptionText: props.subDescriptionText,
    hideOfferMessage: props.hideOfferMessage,
    offerMessageFromPC: props.offerMessageFromPC,
    hideMtoText: props.hideMtoText,
    isFinalSaleProduct,
    handleShopInApp,
    preOrderModalData: props.preOrderModalData,
    isQuickATCModal: props.isQuickATCModal ?? props.isQuickATCForMobile,
    isPostOrderFlow: props.isPostOrderFlow,
  }

  const productCTAProps = {
    type: productCTAType,
    isQuickATCModal: props.isQuickATCModal,
    onAddToCart: props.onAddToCart,
    isAddToCartInProgress: props.isAddToCartInProgress,
    selectedVariantPurchaseInfo: props.selectedVariantPurchaseInfo,
    onJoinWaitList: props.onJoinWaitList,
    showVariantAvailableInventory,
    product: props.product,
    areAllVariantOOS: props.areAllVariantOOS,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- addToCartRef is `any` in ProductFormProps
    addToCartRef: props.addToCartRef,
    isCurrentProductOptionLeavingSoon: props.isCurrentProductOptionLeavingSoon,
    isPostOrderFlow: props.isPostOrderFlow,
    handlePostOrderCallback: props.handlePostOrderCallback,
    isQuickATCForMobile: props.isQuickATCForMobile,
    isKeyboardVisibleForQuickATCForMobile: props.isKeyboardVisibleForQuickATCForMobile,
    setIsKeyboardVisibleForQuickATCForMobile: props.setIsKeyboardVisibleForQuickATCForMobile,
    onClickedShopSimilar: props.onClickedShopSimilar,
    disableAddToCart: props.disableAddToCart,
    selectedOptions: props.selectedOptions,
    setIsCartPopUpOpen: props.setIsCartPopUpOpen,
    isFromCartPopup: props.isFromCartPopup,
    handleShopInApp,
    salePrice: props.festivalOfferPrice ?? props.salePrice,
    subscriptionPrice: props.subscriptionPrice,
    expressPayConfig: props.expressPayConfig,
    enableExpressPayAnimation: true,
    hideKlarna: props.isQuickATCForMobile
      ? false
      : hideKlarna || (props.showPurchaseTypeSelector && props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE),
    purchaseOption: props.purchaseOption,
    selectedPlanId: props.selectedPlanId,
    showPurchaseTypeSelector: props.showPurchaseTypeSelector,
    atcSectionEndRef: props.atcSectionEndRef,
  }

  return (
    <>
      <div ref={productFormRef} className={classes}>
        {props.showBreadCrumb && props.directory && (
          <div className={breadCrumbWrapperDsClass}>
            <BreadCrumb directory={props.directory} deviceType={deviceInfo.deviceType} isPDP={isPDP} />
          </div>
        )}
        <ProductInfoDS {...productInfoProps} />
        {combinedProductOptions.map(option => (
          <Fragment key={option.key}>{option.node}</Fragment>
        ))}
        {showOnlySizeChart && <SizeChartButton onClick={handleSizeChart} isStandalone />}
        {props.isB2BSelfService ? <B2BSelfServeProductOfferMessage /> : <GreyBoxesDS {...greyBoxesProps} />}
        <ProductFormSubscriptionPurchaseSection
          showProductSubscriptionDeliveryMessage={props.showProductSubscriptionDeliveryMessage}
          subscribedPlan={selectedVariant.subscription?.subscribedPlan}
          selectedVariantPurchaseInfoShowWaitList={props.selectedVariantPurchaseInfo?.showWaitList}
          productCTAType={productCTAType}
          isRecurringItemSubscriptionsExpEnabled={isRecurringItemSubscriptionsExpEnabled}
          hidePurchaseTypeSelector={props.hidePurchaseTypeSelector}
          subscriptionEnabled={props.product?.subscriptionEnabled}
          productPurchaseInfo={props.productPurchaseInfo}
          showPurchaseTypeSelector={props.showPurchaseTypeSelector}
          product={props.product}
          purchaseOption={props.purchaseOption}
          setPurchaseOption={props.setPurchaseOption}
          selectedPlanId={props.selectedPlanId}
          setSelectedPlanId={props.setSelectedPlanId}
          isFromCartPopup={props.isFromCartPopup}
          isBuyNow={props.isBuyNow}
          variantSubscriptionPlans={props.variantSubscriptionPlans}
          formattedSubscriptionPrice={props.formattedSubscriptionPrice}
          formattedSalePrice={props.formattedSalePrice}
          formattedFestivalOfferPrice={props.formattedFestivalOfferPrice}
          subscriptionFestivalSavingPercentage={props.subscriptionFestivalSavingPercentage}
          subscriptionId={selectedVariant.subscription?.subscribedPlan?.subscriptionId}
          showVariantAvailableInventory={showVariantAvailableInventory}
          selectedVariantAvailableInventory={selectedVariantAvailableInventory}
          isQuickATCForMobile={props.isQuickATCForMobile}
        />
        {showVariantAvailableInventory && (
          <div className={dsStyles.lowInventoryMessageDsClass}>
            <Icon iconSvg={HurryIcon} className={dsStyles.lowInventoryIconDsClass} />
            {t('hurry_only', { quantity: selectedVariantAvailableInventory })}
          </div>
        )}
        {props.isB2BSelfService && (
          <B2BProductPurchaseOptions
            product={props.product}
            selectedOptions={props.selectedOptions}
            productPurchaseInfo={props.productPurchaseInfo}
          />
        )}
        {!moveAddToCartBtnToRight && !props.isB2BSelfService && (
          <>
            {props.errorMessage && (
              <RenderErrorMessageDS
                errorMessage={props.errorMessage}
                isQuickATCForMobile={props.isQuickATCForMobile}
                className={isMiniPDPPage && props.isQuickATCForMobile ? errorMessageMiniPdpDsClass : undefined}
              />
            )}
            <ProductCTADS {...productCTAProps} />
          </>
        )}
        {showViewFullProductDetails ? <ViewFullProductDetails text={viewFullProductText} /> : <></>}
        {moveAddToCartBtnToRight ? null : (
          <>
            {!hideKlarnaFull && (
              <KlarnaWrapper
                hidePayLater={props.hidePayLater}
                salePrice={props.salePrice}
                festivalOfferPrice={props.festivalOfferPrice}
              />
            )}
            {shouldKeepDefaultDesktopExpressPayRowMounted &&
              isRecurringItemSubscriptionsExpEnabled &&
              props.showPurchaseTypeSelector &&
              props.product?.subscriptionEnabled &&
              props.purchaseOption === PURCHASE_TYPES.SUBSCRIBE && <ProductSubscriptionTermsNotice />}
            {!hideProductDescFooter && !props.isBuyNow && (
              <ProductDescFooter
                hideFooter={props.hideFooter}
                isReturnable={props.isReturnable}
                product={props.product}
                selectedOptions={props.selectedOptions}
                isFinalSaleProduct={isFinalSaleProduct}
                isB2BSelfService={props.isB2BSelfService}
              />
            )}
          </>
        )}
        {showProductDescription && (
          <ProductDescription
            description={props.description ?? ''}
            productDetailsIcons={productDetailsIcons}
            showReadMoreInDesc={showReadMoreInDesc}
            isB2BMode={props.isB2BMode}
          />
        )}
      </div>
      <RightMiniPDPAddToCart
        shouldShow={moveAddToCartBtnToRight}
        hidePayLater={props.hidePayLater}
        salePrice={props.salePrice}
        festivalOfferPrice={props.festivalOfferPrice}
        type={productCTAType}
        onAddToCart={props.onAddToCart}
        isAddToCartInProgress={props.isAddToCartInProgress}
        selectedVariantPurchaseInfo={props.selectedVariantPurchaseInfo}
        onJoinWaitList={props.onJoinWaitList}
        showVariantAvailableInventory={showVariantAvailableInventory}
        product={props.product}
        hideFooter={props.hideFooter}
        isReturnable={props.isReturnable}
        setShowFullPDP={setShowFullPDP}
        hideKlarna={hideKlarnaFull}
        errorMessage={props.errorMessage}
        inStockMsgClass={inStockMsgClass}
        purchaseOption={props.purchaseOption}
        selectedPlanId={props.selectedPlanId}
        showPurchaseTypeSelector={props.showPurchaseTypeSelector}
      />
      {props.sizeChart && showSizeChart && (
        <SizeGuide
          showSizeChartModal={showSizeChart}
          sizeChart={props.sizeChart}
          mergedSizeChart={props.mergedSizeChart ?? []}
          onSizeChartModalClose={() => {
            setShowSizeChart(false)
          }}
          measuringGuides={props.measuringGuides ?? []}
          internationalSizeConversionGuides={props.internationalSizeConversionGuides ?? []}
          product={props.product}
        />
      )}
      {!props.shouldDisableAirlineCompatibility && showAirlineCompatibility && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatbility}
          airlineCompatibilityListingCS={props.airlineCompatibilityListings}
        />
      )}
      <AppQRModal
        isOpen={isAppQRModalOpen}
        product={props.product}
        selectedOptions={props.selectedOptions}
        onClose={handleCloseAppQRModal}
      />
    </>
  )
})

ProductFormDS.displayName = 'ProductFormDS'

export { ProductFormDS }
export default ProductFormDS
